import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { getUserId } from "../../../../core/Global";
import { FontAwesomeIcons } from "../../../../core/Types";

// The jobs board's views. Their filters, group/view-by and Find text live in the
// query string, so path + search is the whole state of the board.
const BOARD_PATHS = ["/boards/jobs", "/boards/grid", "/boards/calendar", "/boards/map"];
const DEFAULT_LINK = "/boards/jobs";

const storageKey = () => "rinvox-jobs-board-" + (getUserId() ?? "anon");

const isBoardPath = (pathname?: string) =>
    !!pathname && BOARD_PATHS.includes(pathname.toLowerCase().replace(/\/+$/, ""));

/**
 * Where "Jobs" should take this person: the board as they last left it -- the same view, the
 * same filters, the same Find text -- or the plain list when there is nothing remembered.
 *
 * Pass the current location from a component that renders with it: while the board is open,
 * the address bar is the newest state, and the remembered copy is only written after render.
 */
export const jobsBoardLink = (current?: { pathname: string, search: string }) => {
    if (current && isBoardPath(current.pathname)) return current.pathname + current.search;

    try {
        const saved = localStorage.getItem(storageKey());
        if (saved) {
            const url = new URL(saved, window.location.origin);
            if (isBoardPath(url.pathname)) return url.pathname + url.search;
        }
    }
    catch { }

    return DEFAULT_LINK;
}

/** Remembers the board's view and query string in this browser while one of its views is open. */
export const useRememberJobsBoard = () => {
    const location = useLocation();

    useEffect(() => {
        if (!isBoardPath(location.pathname)) return;
        try {
            localStorage.setItem(storageKey(), location.pathname + location.search);
        }
        catch { }
    }, [location.pathname, location.search]);
}


// The board's views, in the order the View menu lists them.
export type ViewMode = {
    id: string
    title: string
    icon: FontAwesomeIcons
    link: string
}

export const BOARD_VIEW_MODES: ViewMode[] = [
    { id: "list", title: "List View", icon: "list", link: "/boards/jobs" },
    { id: "grid", title: "Grid View", icon: "grid-2", link: "/boards/grid" },
    { id: "calendar", title: "Calendar View", icon: "calendar-days", link: "/boards/calendar" },
    { id: "map", title: "Map View", icon: "map-location-dot", link: "/boards/map" },
]
